import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { subscriptionAPI, usageAPI, billingAPI } from '../lib/api';
import { getDashboardPath } from '../lib/navigation';
import AlertDialog from '../components/AlertDialog';
import ConfirmDialog from '../components/ConfirmDialog';

const MySubscription = () => {
    const navigate = useNavigate();
    const { tenantId } = useParams();
    const [subscription, setSubscription] = useState(null);
    const [addons, setAddons] = useState([]);
    const [usage, setUsage] = useState(null);
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [cancelling, setCancelling] = useState(false);
    const [alert, setAlert] = useState({ isOpen: false, title: '', message: '', type: 'info' });
    const [confirm, setConfirm] = useState({ isOpen: false, title: '', message: '', onConfirm: null });

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const dashboardPath = getDashboardPath(tenantId, user.role);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [subscriptionData, addonsData, usageData, alertsData] = await Promise.all([
                subscriptionAPI.getMySubscription().catch(() => null),
                subscriptionAPI.getMyAddons().catch(() => []),
                usageAPI.getUsageSummary().catch(() => null),
                usageAPI.getUsageAlerts().catch(() => [])
            ]);
            setSubscription(subscriptionData);
            setAddons(addonsData);
            setUsage(usageData);
            setAlerts(alertsData);
        } catch (error) {
            console.error('Failed to load data:', error);
            setAlert({ isOpen: true, title: 'Error', message: error.message, type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = (immediate) => {
        setConfirm({
            isOpen: true,
            title: immediate ? 'Cancel Subscription Immediately' : 'Cancel Subscription',
            message: immediate
                ? 'Are you sure you want to cancel your subscription immediately? You will lose access to all features right away.'
                : 'Are you sure you want to cancel your subscription? It will remain active until the end of your billing period.',
            type: 'danger',
            onConfirm: () => performCancel(immediate)
        });
    };

    const performCancel = async (immediate) => {
        setCancelling(true);
        try {
            await subscriptionAPI.cancelSubscription(immediate);
            setAlert({
                isOpen: true,
                title: 'Success',
                message: immediate ? 'Subscription cancelled immediately' : 'Subscription will be cancelled at the end of the billing period',
                type: 'success'
            });
            loadData();
        } catch (error) {
            console.error('Cancellation failed:', error);
            setAlert({ isOpen: true, title: 'Error', message: error.message || 'Cancellation failed', type: 'error' });
        } finally {
            setCancelling(false);
        }
    };

    const handleRenew = () => {
        navigate(`/${tenantId}/subscription/plans`);
    };

    const getUsageColor = (percentage) => {
        if (percentage >= 100) return 'red';
        if (percentage >= 90) return 'orange';
        if (percentage >= 80) return 'yellow';
        return 'green';
    };

    const formatBytes = (bytes) => {
        if (!bytes) return '0 B';
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
        return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="p-6">
            {/* Header */}
            <div className="mb-8">
                <button
                    onClick={() => navigate(dashboardPath)}
                    className="text-blue-600 hover:text-blue-800 mb-4 flex items-center"
                >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                    </svg>
                    Back to Dashboard
                </button>
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">My Subscription</h1>
                        <p className="text-gray-600 mt-1">Manage your subscription and add-ons</p>
                    </div>
                    <button
                        onClick={() => navigate(`/${tenantId}/subscription/plans`)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                        View All Plans
                    </button>
                </div>
            </div>

            {/* Usage Alerts */}
            {alerts.length > 0 && (
                <div className="mb-8 space-y-4">
                    {alerts.map((alertItem) => (
                        <div
                            key={alertItem.id}
                            className={`p-4 rounded-lg border ${
                                alertItem.alertLevel === 'EXCEEDED'
                                    ? 'bg-red-50 border-red-300'
                                    : alertItem.alertLevel === 'CRITICAL'
                                        ? 'bg-orange-50 border-orange-300'
                                        : 'bg-yellow-50 border-yellow-300'
                            }`}
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                    <svg className={`w-6 h-6 mr-3 ${
                                        alertItem.alertLevel === 'EXCEEDED' ? 'text-red-600' :
                                        alertItem.alertLevel === 'CRITICAL' ? 'text-orange-600' :
                                        'text-yellow-600'
                                    }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                    <div>
                                        <h4 className={`font-semibold ${
                                            alertItem.alertLevel === 'EXCEEDED' ? 'text-red-800' :
                                            alertItem.alertLevel === 'CRITICAL' ? 'text-orange-800' :
                                            'text-yellow-800'
                                        }`}>
                                            {alertItem.usageType === 'STORAGE' ? 'Storage' : 'Investor Links'} {alertItem.alertLevel}
                                        </h4>
                                        <p className={`text-sm ${
                                            alertItem.alertLevel === 'EXCEEDED' ? 'text-red-600' :
                                            alertItem.alertLevel === 'CRITICAL' ? 'text-orange-600' :
                                            'text-yellow-600'
                                        }`}>
                                            {alertItem.message} - {alertItem.displayUsed} / {alertItem.displayLimit}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => navigate(`/${tenantId}/subscription/plans`)}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                                >
                                    Upgrade Storage
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Subscription Details */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Current Plan */}
                    {subscription && subscription.id ? (
                        <div className="bg-white rounded-lg shadow p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-xl font-bold text-gray-900">Current Plan</h2>
                                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                                    subscription.status === 'ACTIVE'
                                        ? 'bg-green-100 text-green-800'
                                        : subscription.status === 'CANCELLED'
                                            ? 'bg-gray-100 text-gray-800'
                                            : 'bg-red-100 text-red-800'
                                }`}>
                                    {subscription.status}
                                </span>
                            </div>

                            <div className="space-y-4">
                                <div className="flex justify-between py-3 border-b">
                                    <span className="text-gray-600">Plan</span>
                                    <span className="font-semibold">{subscription.plan?.planName}</span>
                                </div>
                                <div className="flex justify-between py-3 border-b">
                                    <span className="text-gray-600">Price</span>
                                    <span className="font-semibold">
                                        {subscription.plan?.basePrice === 0 ? 'Free' : `₹${subscription.plan?.basePrice}/month`}
                                    </span>
                                </div>
                                <div className="flex justify-between py-3 border-b">
                                    <span className="text-gray-600">Storage</span>
                                    <span className="font-semibold">{subscription.plan?.storageLimitGb} GB</span>
                                </div>
                                {subscription.plan?.investorLinkLimit > 0 && (
                                    <div className="flex justify-between py-3 border-b">
                                        <span className="text-gray-600">Investor Links</span>
                                        <span className="font-semibold">{subscription.plan?.investorLinkLimit}</span>
                                    </div>
                                )}
                                <div className="flex justify-between py-3 border-b">
                                    <span className="text-gray-600">Started On</span>
                                    <span className="font-semibold">
                                        {new Date(subscription.startDate).toLocaleDateString()}
                                    </span>
                                </div>
                                <div className="flex justify-between py-3 border-b">
                                    <span className="text-gray-600">Renews On</span>
                                    <span className="font-semibold">
                                        {subscription.endDate ? new Date(subscription.endDate).toLocaleDateString() : 'N/A'}
                                    </span>
                                </div>
                                <div className="flex justify-between py-3">
                                    <span className="text-gray-600">Auto-Renew</span>
                                    <span className={`font-semibold ${subscription.autoRenew ? 'text-green-600' : 'text-red-600'}`}>
                                        {subscription.autoRenew ? 'Enabled' : 'Disabled'}
                                    </span>
                                </div>
                            </div>

                            <div className="flex space-x-4 mt-6">
                                {subscription.status === 'ACTIVE' ? (
                                    <>
                                        <button
                                            onClick={() => handleCancel(false)}
                                            disabled={cancelling}
                                            className="flex-1 py-2 px-4 border border-red-600 text-red-600 rounded-lg hover:bg-red-50"
                                        >
                                            Cancel at Period End
                                        </button>
                                        <button
                                            onClick={() => handleCancel(true)}
                                            disabled={cancelling}
                                            className="flex-1 py-2 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700"
                                        >
                                            Cancel Immediately
                                        </button>
                                    </>
                                ) : (
                                    <button
                                        onClick={handleRenew}
                                        className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                    >
                                        Renew Subscription
                                    </button>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="bg-white rounded-lg shadow p-6 text-center">
                            <h2 className="text-xl font-bold text-gray-900 mb-4">No Active Subscription</h2>
                            <p className="text-gray-600 mb-6">You don't have an active subscription yet.</p>
                            <button
                                onClick={() => navigate(`/${tenantId}/subscription/plans`)}
                                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                                Browse Plans
                            </button>
                        </div>
                    )}

                    {/* Purchased Addons */}
                    {addons.length > 0 && (
                        <div className="bg-white rounded-lg shadow p-6">
                            <h2 className="text-xl font-bold text-gray-900 mb-6">Purchased Add-ons</h2>
                            <div className="space-y-4">
                                {addons.map((addon) => (
                                    <div key={addon.id} className="flex items-center justify-between py-3 border-b last:border-b-0">
                                        <div>
                                            <h3 className="font-semibold">{addon.addon?.addonName}</h3>
                                            <p className="text-sm text-gray-500">
                                                Quantity: {addon.quantity} | Purchased: {new Date(addon.purchaseDate).toLocaleDateString()}
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-semibold">{addon.totalAmount}</div>
                                            <span className={`px-2 py-1 rounded text-xs ${
                                                addon.status === 'ACTIVE' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                            }`}>
                                                {addon.status}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Usage Summary */}
                <div className="space-y-6">
                    {usage && (
                        <>
                            {/* Storage Usage */}
                            <div className="bg-white rounded-lg shadow p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Storage Usage</h3>
                                {usage.storageUsage && (
                                    <div>
                                        <div className="flex justify-between mb-2">
                                            <span className="text-gray-600">Used</span>
                                            <span className="font-semibold">
                                                {usage.storageUsage.displayUsed} / {usage.storageUsage.displayLimit}
                                            </span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                                            <div
                                                className="h-full transition-all"
                                                style={{
                                                    width: `${Math.min(usage.storageUsage.percentage, 100)}%`,
                                                    backgroundColor: getUsageColor(usage.storageUsage.percentage) === 'green' ? '#10b981' :
                                                                   getUsageColor(usage.storageUsage.percentage) === 'yellow' ? '#f59e0b' :
                                                                   getUsageColor(usage.storageUsage.percentage) === 'orange' ? '#f97316' :
                                                                   '#ef4444'
                                                }}
                                            />
                                        </div>
                                        <div className="text-right text-sm mt-1 text-gray-500">
                                            {usage.storageUsage.percentage.toFixed(1)}%
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Investor Link Usage */}
                            {usage.investorLinkUsage && (
                                <div className="bg-white rounded-lg shadow p-6">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Investor Links</h3>
                                    <div>
                                        <div className="flex justify-between mb-2">
                                            <span className="text-gray-600">Used</span>
                                            <span className="font-semibold">
                                                {usage.investorLinkUsage.used} / {usage.investorLinkUsage.limit}
                                            </span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                                            <div
                                                className="h-full transition-all"
                                                style={{
                                                    width: `${Math.min(usage.investorLinkUsage.percentage, 100)}%`,
                                                    backgroundColor: getUsageColor(usage.investorLinkUsage.percentage) === 'green' ? '#10b981' :
                                                                   getUsageColor(usage.investorLinkUsage.percentage) === 'yellow' ? '#f59e0b' :
                                                                   getUsageColor(usage.investorLinkUsage.percentage) === 'orange' ? '#f97316' :
                                                                   '#ef4444'
                                                }}
                                            />
                                        </div>
                                        <div className="text-right text-sm mt-1 text-gray-500">
                                            {usage.investorLinkUsage.percentage.toFixed(1)}%
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {/* Quick Actions */}
                    <div className="bg-white rounded-lg shadow p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
                        <div className="space-y-3">
                            <button
                                onClick={() => navigate(`/${tenantId}/subscription/plans`)}
                                className="w-full py-2 px-4 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 text-left"
                            >
                                → View Available Plans
                            </button>
                            <button
                                onClick={() => navigate(`/${tenantId}/billing`)}
                                className="w-full py-2 px-4 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 text-left"
                            >
                                → View Billing History
                            </button>
                            <button
                                onClick={() => navigate(`/${tenantId}/purchase-addon`)}
                                className="w-full py-2 px-4 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 text-left"
                            >
                                → Purchase Add-ons
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Alert Dialog */}
            <AlertDialog
                isOpen={alert.isOpen}
                onClose={() => setAlert({ ...alert, isOpen: false })}
                title={alert.title}
                message={alert.message}
                type={alert.type}
            />

            {/* Confirm Dialog */}
            <ConfirmDialog
                isOpen={confirm.isOpen}
                onClose={() => setConfirm({ ...confirm, isOpen: false })}
                onConfirm={confirm.onConfirm || (() => {})}
                title={confirm.title}
                message={confirm.message}
                type={confirm.type}
            />
        </div>
    );
};

export default MySubscription;
